import React, { useEffect, useState } from "react";
import { Table, Select, message, Typography } from "antd";
import type { TableColumnsType, TableProps } from "antd";
import axios from "axios";
import {
  ResponseAPI,
  OptionFilterItem,
  FilterCombustion,
  CombustionManualFuelVolume,
  Month,
} from "src/entity/report-fuel-volume/report-fuel-volume.entity";
import { getReportFuelVolumeOptionFilter, getReportFuelVolume } from "src/services/combustion.service";
import { profileStore } from "src/stores/profile-store";
import { useShallow } from "zustand/react/shallow";
import { canViewTable } from "../../PermissionCheck/PermissionCheck";
import useNavigationMenu from "src/hook/useNavigationMenu";
import { excelStore } from "src/stores/excel-store.interface";
import { CommonError } from "src/entity/common/menus-enum.enum";
import { parseAndFormatScientificNumber } from "src/utils/process-number";
import { generateYearArray } from "src/utils/array";
import { addCenterAlign } from "src/components/AntdCustomPart/TableTitle.tsx/TableTitle";

const { Text } = Typography;

const ReportFuelVolumeTable = () => {
  const profile = profileStore(useShallow((state) => state.profile));
  const [messageApi, contextHolder] = message.useMessage();
  const [companyId, setCompanyId] = useState<string>("");
  const [data, setData] = useState<CombustionManualFuelVolume[]>();
  const currentYear = new Date().getFullYear();
  const [optionYears, setOptionYears] = useState<number[]>([]);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [filterStackId, setFilterStackId] = useState<number[]>();
  const [filterSourceId, setFilterSourceId] = useState<number[]>();
  const [filterArea, setFilterArea] = useState<string[]>();
  const [filterFuelName, setFilterFuelName] = useState<string[]>();
  const [filterCombustion, setFilterCombustion] = useState<FilterCombustion>({
    stackId: null,
    sourceId: null,
    area: null,
    fuelName: null,
  });
  const [reportInprogress, setReportInprogress] = useState<boolean>(false);
  const { permissions } = useNavigationMenu();
  const excelSetSelectedYear = excelStore(useShallow((state) => state.setSelectedYear));
  const excelSetQueryStr = excelStore(useShallow((state) => state.setQueryStr));

  const fetchYear = () => {
    const years = generateYearArray();
    setOptionYears(years as number[]);
    setSelectedYear(years[0] as number);
  };

  const calPagination = (current?: number) => {
    if (current) {
      return (current - 1) * 10;
    }
  };

  const createParamFilter = (type: "fetch" | "export") => {
    let queryParam = "";

    let offset = 0;
    let limit = pageSize;
    if (selectedYear) {
      queryParam += `year=${selectedYear}`;
    }

    if (type == "fetch") {
      if (current) {
        let resultCalOffset = calPagination(current);
        if (resultCalOffset) {
          offset = resultCalOffset;
        }
        queryParam += `&offset=${offset}`;
      }
      if (limit) {
        queryParam += `&limit=${limit}`;
      }
    }

    if (filterCombustion) {
      const str = Object.entries(filterCombustion)
        .filter(([_, value]) => value != null && value.length > 0)
        .map(([key, value]) => `&${key}=[${[value.join(",")]}]`)
        .join("");
      queryParam += str;
    }

    return queryParam;
  };

  const fetchCombustions = async () => {
    try {
      if (companyId) {
        setLoading(true);
        let offset = 0;
        let resultCalOffset = calPagination(current);
        if (resultCalOffset) {
          offset = resultCalOffset;
        }
        const queryParam = createParamFilter("fetch");
        if (selectedYear) {
          let response = await getReportFuelVolume(companyId, queryParam);
          let responseData: ResponseAPI = response.data;
          if (responseData.code === 200) {
            setLoading(false);
            let combustions = responseData.data.combustions.map((el, index) => {
              return {
                no: (index + offset + 1).toString(),
                ...el,
              };
            });
            setData([...combustions]);
            setTotal(responseData.data.total);
            const reportInprogress = combustions.filter((item) => item.statusCalulationFuelVolume === "inprogress");
            if (reportInprogress.length > 0) {
              setReportInprogress(true);
            } else {
              setReportInprogress(false);
            }
          }
        }
      }
    } catch (error: any) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOptionFilterCombustions = async () => {
    try {
      if (companyId) {
        let query = `year=${selectedYear}`;
        let response = await getReportFuelVolumeOptionFilter(companyId, query);
        let responseData: ResponseAPI = response.data;
        if (responseData.code === 200) {
          const filter = response.data.data.filter;
          if (filter.length > 0) {
            setFilterStackId(filter.find((item: OptionFilterItem) => item.key === "stackId")?.filter ?? []);
            setFilterSourceId(filter.find((item: OptionFilterItem) => item.key === "sourceId")?.filter ?? []);
            setFilterArea(filter.find((item: OptionFilterItem) => item.key === "area")?.filter ?? []);
            setFilterFuelName(filter.find((item: OptionFilterItem) => item.key === "fuelName")?.filter ?? []);
          } else {
            setFilterStackId([]);
            setFilterSourceId([]);
            setFilterArea([]);
            setFilterFuelName([]);
          }
        }
      }
    } catch (error: any) {
      console.error(error);
    }
  };

  const handleYearChange = (value: number) => {
    setSelectedYear(value);
  };

  const exportExel = async () => {
    const token = localStorage.getItem("auth-token");
    if (selectedYear) {
      const queryParam = createParamFilter("export");
      axios({
        method: "get",
        url: `${process.env.REACT_APP_BASE_URL}api/combustion/report/fuel-volume/export-excel/${companyId}?${queryParam}`,
        responseType: "blob",
        headers: {
          Authorization: "Bearer " + token,
        },
      })
        .then((response) => {
          const link: any = document.createElement("a");
          link.href = window.URL.createObjectURL(new Blob([response.data], { type: "application/xlsx" }));
          const fileName = `FuelVolume-Report-${selectedYear}.xlsx`;
          link.download = fileName;
          document.body.appendChild(link);

          link.click();
          setTimeout(function () {
            window.URL.revokeObjectURL(link);
          }, 200);
        })
        .catch(() => {
          messageApi.open({
            type: "error",
            content: CommonError.SOMETHING_WRONG,
          });
        });
    }
  };

  const resetFilter = () => {
    setFilterCombustion({});
  };

  useEffect(() => {
    if (selectedYear) {
      excelSetSelectedYear(selectedYear);
    }
  }, [selectedYear]);

  useEffect(() => {
    const queryParam = createParamFilter("export");
    if (queryParam) {
      excelSetQueryStr(queryParam);
    }
  }, [filterCombustion, selectedYear]);

  useEffect(() => {
    fetchYear();
  }, []);

  useEffect(() => {
    if (profile?.activeCompany?._id) {
      setCompanyId(profile.activeCompany._id);
    }
  }, [profile?.activeCompany]);

  useEffect(() => {
    setSelectedYear(currentYear);
    setCurrent(1);
    fetchCombustions();
  }, [companyId]);

  useEffect(() => {
    resetFilter();
    setCurrent(1);
  }, [selectedYear]);

  useEffect(() => {
    if (selectedYear) {
      const fetchData = async () => {
        fetchCombustions();
        fetchOptionFilterCombustions();
      };

      fetchData();
    }
  }, [current, filterCombustion]);

  const months: Month[] = [
    { title: "Jan", dataIndex: "jan" },
    { title: "Feb", dataIndex: "feb" },
    { title: "Mar", dataIndex: "mar" },
    { title: "Apr", dataIndex: "apr" },
    { title: "May", dataIndex: "may" },
    { title: "Jun", dataIndex: "jun" },
    { title: "Jul", dataIndex: "jul" },
    { title: "Aug", dataIndex: "aug" },
    { title: "Sep", dataIndex: "sep" },
    { title: "Oct", dataIndex: "oct" },
    { title: "Nov", dataIndex: "nov" },
    { title: "Dec", dataIndex: "dec" },
  ];

  const monthColumns: TableColumnsType<CombustionManualFuelVolume> = months.map((month) => ({
    title: month.title,
    dataIndex: "",
    render: (_, record) => {
      if (record && typeof record === "object") {
        const value = record[month.dataIndex];

        if (typeof value === "number") {
          return value ? <Text>{parseAndFormatScientificNumber(`${value}`, 2)}</Text> : 0;
        } else {
          return <Text>0</Text>;
        }
      }
    },
  }));

  const staticColumns: TableColumnsType<CombustionManualFuelVolume> = [
    {
      title: "No",
      dataIndex: "no",
      width: 40,
      render: (text) => <Text>{text}</Text>,
    },
    {
      title: "Stack ID",
      dataIndex: "stackId",
      width: 70,
      filteredValue: filterCombustion?.stackId || null,
      filters: filterStackId?.map((el: number) => ({ text: el, value: el })),
      render: (text) => <Text>{text}</Text>,
    },
    {
      title: "Source ID",
      dataIndex: "sourceId",
      width: 70,
      defaultSortOrder: "descend",
      filteredValue: filterCombustion?.sourceId || null,
      filters: filterSourceId?.map((el: number) => ({ text: el, value: el })),
      render: (text) => <Text>{text}</Text>,
    },
    {
      title: "Area",
      dataIndex: "area",
      width: 50,
      filteredValue: filterCombustion?.area || null,
      filters: filterArea?.map((el: string) => ({ text: el, value: el })),
      render: (text) => <Text>{text}</Text>,
    },
    {
      title: "Fuel Name",
      dataIndex: "fuelName",
      filteredValue: filterCombustion?.fuelName || null,
      filters: filterFuelName?.map((el: string) => ({ text: el, value: el })),
      render: (text) => <Text>{text}</Text>,
    },
    {
      title: "Unit",
      width: 40,
      dataIndex: "unit",
      render: (text) => <Text>{text}</Text>,
    },
  ];

  const totalColumns: TableColumnsType<CombustionManualFuelVolume> = [
    {
      title: "Total",
      dataIndex: "total",
      render: (value) => {
        if (typeof value === "number") {
          return value ? <Text>{parseAndFormatScientificNumber(`${value}`, 2)}</Text> : 0;
        } else {
          return <Text>0</Text>;
        }
      },
    },
  ];

  const columns: TableColumnsType<CombustionManualFuelVolume> = [...staticColumns, ...monthColumns, ...totalColumns];

  const onTableChange: TableProps<CombustionManualFuelVolume>["onChange"] = (pagination: any, filters, sorter, extra) => {
    setFilterCombustion((prevState) => ({
      ...prevState,
      ...filters,
    }));
    setCurrent(pagination.current);
    setPageSize(pagination.pageSize);
  };

  return (
    <>
      {contextHolder}
      <div style={{ display: "flex", justifyContent: "space-between", paddingBottom: "10px" }}>
        <div>
          <Select
            value={selectedYear}
            style={{ width: 120 }}
            onChange={handleYearChange}
            options={optionYears.map((year) => ({ label: year, value: year }))}
            loading={loading}
            size="middle"
          />
        </div>
      </div>
      <Table
        style={{ whiteSpace: "nowrap" }}
        columns={addCenterAlign(columns)}
        dataSource={canViewTable(permissions) ? data : []}
        onChange={onTableChange}
        rowKey="_id"
        scroll={{ x: "max-content" }}
        loading={loading || reportInprogress ? true : false}
        pagination={{
          showSizeChanger: false,
          size: "small",
          current,
          pageSize,
          total,
        }}
      />
    </>
  );
};

export default ReportFuelVolumeTable;
